import { Check } from "lucide-react";
import { ReactNode } from "react";

import { cn } from "../../utils/shadcn-ui";

/**
 * Progress indicator for a multi-step flow: numbered markers joined by
 * connectors, the steps already behind you ticked off.
 *
 * Presentational only — it neither owns the step state nor moves between steps,
 * so the flow keeps its own `current` and decides what advancing means. Add
 * click-to-navigate here when a flow appears that can actually go back to an
 * arbitrary step.
 */
export function Stepper({
  steps,
  current,
  label,
}: {
  /** Localized step labels — `<Trans>` belongs in the caller. */
  steps: ReactNode[];
  /** Zero-based index of the step being shown. */
  current: number;
  /** Names the whole sequence for screen readers, e.g. "New user steps". */
  label: string;
}) {
  return (
    <ol aria-label={label} className="mb-6 flex items-center gap-2 text-sm">
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;

        return (
          <li
            key={i}
            // `min-w-0`: a long label shrinks and ellipsizes rather than
            // pushing the later markers off the dialog.
            className="flex min-w-0 flex-1 items-center gap-2"
            aria-current={active ? "step" : undefined}
          >
            <span
              aria-hidden
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                done && "border-brand-primary bg-brand-primary text-white",
                active && "border-brand-primary text-brand-primary",
                !done && !active && "border-border text-muted-foreground",
              )}
            >
              {done ? <Check size={14} /> : i + 1}
            </span>
            <span
              className={cn(
                "truncate",
                active ? "font-semibold" : "text-muted-foreground",
              )}
            >
              {step}
            </span>
            {/* Connector, on every marker but the last. */}
            {i < steps.length - 1 && (
              <span aria-hidden className="bg-border h-px flex-1" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
